import React from 'react'
import { useTar } from '@/modules'
import { ChallengeFilterRail } from './ChallengeFilterRail'
import type { useChallengeBrowser } from '@/hooks/useChallengeBrowser'

interface PanelProps {
  open: boolean
  browser: ReturnType<typeof useChallengeBrowser>
  onClose: () => void
  assignmentsCount?: number
  sandboxesCount?: number
}

// A real full-height sidebar rather than a floating card: a fixed pane pinned to
// the inline-end edge (mirroring the app's menu sidebar on the opposite side),
// paired with an in-flow spacer that animates its width so the grid is *pushed*
// aside instead of overlaid. No backdrop, no z-index war with the menu sidebar.
export const ChallengeFilterPanel = ({ open, browser, onClose, assignmentsCount, sandboxesCount }: PanelProps) => {
  const { t, isRtl } = useTar()
  // Closed position is off the inline-end edge: right in LTR, left in RTL.
  const closedTransform = isRtl ? '-translate-x-full' : 'translate-x-full'

  return (
    <>
      {/* In-flow spacer that reserves the panel's width and pushes the grid. */}
      <div
        aria-hidden
        className={`hidden shrink-0 transition-[width] duration-300 ease-out md:block ${open ? 'w-[240px]' : 'w-0'}`}
      />
      {/* The pane itself — fixed, full viewport height, flush to the edge. */}
      <aside
        aria-label={t('challenges.filters')}
        aria-hidden={!open}
        className={`fixed inset-y-0 end-0 z-30 hidden w-[240px] flex-col border-s border-border bg-card transition-transform duration-300 ease-out md:flex ${open ? 'translate-x-0' : closedTransform}`}
      >
        <div className="flex-1 overflow-y-auto p-4">
          <ChallengeFilterRail browser={browser} onClose={onClose} assignmentsCount={assignmentsCount} sandboxesCount={sandboxesCount} />
        </div>
      </aside>
    </>
  )
}
